(() => {
  'use strict';
  if (customElements.get('dv-product')) return;
  class DormVibesProduct extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.strings = JSON.parse(this.querySelector('[data-dv-strings]').textContent);
      this.sequence = 0;
      this.loading = false;
      this.failed = false;
      this.expandedColors = new Set();
      this.controller = new AbortController();
      this.addEventListener('click', event => this.onClick(event), { signal: this.controller.signal });
      this.addEventListener('submit', event => this.onSubmit(event), { signal: this.controller.signal });
      this.addEventListener('keydown', event => this.onKey(event), { signal: this.controller.signal });
      this.addEventListener('change', event => {
        if (event.target.name === 'properties[Pillow sham style]') this.syncSelectionSummary();
      }, { signal: this.controller.signal });
      const dialog = this.querySelector('[data-dv-dialog]');
      dialog.addEventListener('close', () => this.restoreScroll(), { signal: this.controller.signal });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      }, { signal: this.controller.signal });
      window.addEventListener('pageshow', () => { this.submitting = false; this.syncState(); }, { signal: this.controller.signal });
      window.addEventListener('popstate', () => window.location.reload(), { signal: this.controller.signal });
      this.syncState();
      this.prepareSwatches();
      this.observePurchase();
      this.observeReviews();
      this.setAttribute('data-enhanced', '');
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      this.purchaseObserver?.disconnect();
      this.reviewObserver?.disconnect();
      this.restoreScroll();
      this.ready = false;
    }
    readState() {
      return JSON.parse(this.querySelector('[data-dv-state]').textContent);
    }
    syncState() {
      this.state = this.readState();
      this.optionIds = [...this.state.optionValueIds];
      const primary = this.querySelector('[data-dv-add]');
      const unavailable = !this.state.variantId || !this.state.available || this.state.blocked;
      primary.disabled = unavailable || this.loading || this.failed || this.submitting;
      const stickyButton = this.querySelector('[data-dv-sticky-add]');
      stickyButton.disabled = primary.disabled;
      stickyButton.textContent = this.loading ? this.strings.loading : primary.querySelector('[data-dv-add-label]').textContent;
      this.querySelector('[data-dv-sticky-price]').textContent = this.querySelector('[data-dv-price]').textContent;
      this.syncSelectionSummary();
      this.showMedia(this.state.mediaId);
      this.syncSticky();
    }
    syncSticky() {
      const sticky = this.querySelector('[data-dv-sticky]');
      sticky.hidden = this.dataset.sticky !== 'true' || !this.buyAbove || this.getBoundingClientRect().bottom <= 0;
    }
    syncSelectionSummary() {
      const sham = this.querySelector('[name="properties[Pillow sham style]"]:checked')?.value;
      this.querySelector('[data-dv-sticky-selection]').textContent = [this.state.selection, sham].filter(Boolean).join(' / ');
    }
    observePurchase() {
      this.purchaseObserver?.disconnect();
      if (!('IntersectionObserver' in window)) return;
      this.purchaseObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target.matches('.dv-purchase-row')) this.buyAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        }
        this.syncSticky();
      });
      this.purchaseObserver.observe(this.querySelector('.dv-purchase-row'));
      this.purchaseObserver.observe(this);
    }
    observeReviews() {
      const app = this.querySelector('[data-dv-review-app]');
      const fallback = this.querySelector('[data-dv-review-fallback]');
      if (!app || !fallback) return;
      const refresh = () => { fallback.hidden = !!app.querySelector('iframe, .loox-reviews-list, .loox-grid, .loox-review') || app.textContent.trim().length > 0; };
      refresh();
      this.reviewObserver = new MutationObserver(refresh);
      this.reviewObserver.observe(app, { subtree: true, childList: true, characterData: true });
    }
    prepareSwatches() {
      const colors = { white: '#f4f2ed', 'dark gray': '#555359', 'dark grey': '#555359', 'light gray': '#acaeb5', 'light grey': '#acaeb5', gray: '#8c8c8c', grey: '#8c8c8c', black: '#232225', beige: '#c6ad8d', aqua: '#63c6c3', blue: '#6583af', 'light blue': '#a9c9e4', 'dark red': '#782c3b', red: '#c54253', coffee: '#92705d', purple: '#8b69b8', pink: '#e5a0bc', blush: '#d9bdc5', green: '#4b795d', mint: '#a2c9b9', navy: '#293c61', chocolate: '#604537', teal: '#368a8a', yellow: '#efcd56', gold: '#b58442', brown: '#795f4e', turquoise: '#54bec1' };
      this.querySelectorAll('[data-dv-swatch]').forEach(node => {
        const color = colors[node.dataset.dvSwatch.toLowerCase()];
        if (color) node.style.backgroundColor = color;
        else node.hidden = true;
      });
    }
    onClick(event) {
      const colorToggle = event.target.closest('[data-dv-color-toggle]');
      if (colorToggle) {
        const position = colorToggle.dataset.dvColorToggle;
        if (this.expandedColors.has(position)) this.expandedColors.delete(position);
        else this.expandedColors.add(position);
        this.syncColorLists();
        return;
      }
      const option = event.target.closest('[data-dv-option]');
      if (option && this.contains(option)) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        const target = new URL(option.href, window.location.href);
        if (target.origin !== window.location.origin) return;
        if (new URL(option.dataset.productUrl, window.location.href).pathname !== new URL(this.dataset.productUrl, window.location.href).pathname) return;
        event.preventDefault();
        this.updateOption(option);
        return;
      }
      const thumb = event.target.closest('[data-dv-thumb]');
      if (thumb) { this.showMedia(thumb.dataset.dvThumb); return; }
      if (event.target.closest('[data-dv-prev]')) { this.stepPhoto(-1); return; }
      if (event.target.closest('[data-dv-next]')) { this.stepPhoto(1); return; }
      const zoom = event.target.closest('[data-dv-zoom]');
      if (zoom && typeof this.querySelector('[data-dv-dialog]').showModal === 'function') {
        event.preventDefault();
        const img = zoom.querySelector('img');
        const full = this.querySelector('[data-dv-zoom-image]');
        full.src = zoom.href;
        full.alt = img.alt;
        this.querySelector('[data-dv-zoom-caption]').textContent = img.alt || this.querySelector('h1').textContent;
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.querySelector('[data-dv-dialog]').showModal();
        return;
      }
      if (event.target.closest('[data-dv-close]')) { this.querySelector('[data-dv-dialog]').close(); return; }
      if (event.target.closest('[data-dv-minus], [data-dv-plus]')) {
        const quantity = this.querySelector('[name=quantity]');
        const direction = event.target.closest('[data-dv-plus]') ? 1 : -1;
        quantity.value = this.validQuantity(quantity, Number(quantity.value) + direction * Number(quantity.step || 1));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (event.target.closest('[data-dv-sticky-add]')) {
        const form = this.querySelector('.dv-native-form');
        if (!form.checkValidity()) { form.scrollIntoView({ behavior: 'smooth', block: 'center' }); form.reportValidity(); return; }
        if (!this.loading && !this.failed && !this.submitting && this.state.available && !this.state.blocked) form.requestSubmit(this.querySelector('[data-dv-add]'));
      }
    }
    onKey(event) {
      if (event.target.closest('[data-dv-gallery]') && ['ArrowLeft', 'ArrowRight'].includes(event.key) && !event.target.closest('video,model-viewer')) {
        event.preventDefault();
        this.stepPhoto(event.key === 'ArrowLeft' ? -1 : 1);
      }
    }
    validQuantity(input, desired) {
      const min = Number(input.min || 1), step = Number(input.step || 1), max = input.max ? Number(input.max) : Infinity;
      const value = Number.isFinite(desired) ? desired : min;
      const maximum = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity;
      return Math.min(maximum, Math.max(min, min + Math.floor((value - min) / step) * step));
    }
    syncColorLists() {
      this.querySelectorAll('[data-dv-color-toggle]').forEach(button => {
        const position = button.dataset.dvColorToggle;
        const expanded = this.expandedColors.has(position);
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? this.strings.fewerColors : this.strings.allColors;
        this.querySelector(`[data-dv-colors-list="${position}"]`).dataset.collapsed = String(!expanded);
      });
    }
    onSubmit(event) {
      if (!event.target.matches('.dv-native-form')) return;
      const form = event.target;
      if (this.loading || this.failed || this.submitting || !this.state.available || this.state.blocked || !this.state.variantId || !form.reportValidity()) {
        event.preventDefault();
        if (!this.loading) this.showMessage(this.failed ? this.strings.error : this.strings.formError);
        return;
      }
      this.submitting = true;
      this.querySelector('[data-dv-add]').disabled = true;
      this.querySelector('[data-dv-sticky-add]').disabled = true;
    }
    showMessage(message, reloadUrl) {
      const node = this.querySelector('[data-dv-message]');
      node.replaceChildren(document.createTextNode(message));
      if (reloadUrl) {
        const link = document.createElement('a');
        link.href = reloadUrl;
        link.textContent = this.strings.reload;
        node.append(link);
      }
      node.hidden = false;
    }
    async updateOption(option) {
      const position = Number(option.dataset.position) - 1;
      if (!option.dataset.valueId || !Number.isInteger(position)) { window.location.assign(option.href); return; }
      this.optionIds[position] = option.dataset.valueId;
      const url = new URL(this.dataset.productUrl, window.location.href);
      url.search = window.location.search;
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', this.optionIds.join(','));
      const visibleUrl = new URL(url);
      url.searchParams.set('section_id', this.dataset.section);
      this.request?.abort();
      const sequence = ++this.sequence;
      this.request = new AbortController();
      this.loading = true;
      this.failed = false;
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-dv-add]').disabled = true;
      this.querySelector('[data-dv-sticky-add]').disabled = true;
      this.querySelector('[data-dv-sticky-add]').textContent = this.strings.loading;
      const properties = [...this.querySelectorAll('[data-dv-properties]')];
      const quantity = Number(this.querySelector('[name=quantity]').value);
      const oldPlan = this.querySelector('[name=selling_plan]')?.value;
      const focusKey = { position: option.dataset.position, id: option.dataset.valueId };
      try {
        const response = await fetch(url.href, { signal: AbortSignal.any([this.request.signal, AbortSignal.timeout(15000)]), credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Section unavailable');
        const html = await response.text();
        if (sequence !== this.sequence || !this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = parsed.getElementById(this.id);
        const nextPurchase = nextRoot?.querySelector('[data-dv-purchase]');
        if (!nextPurchase || !nextPurchase.querySelector('[data-dv-state]')) throw new Error('Invalid section response');
        const nextState = JSON.parse(nextPurchase.querySelector('[data-dv-state]').textContent);
        if (!Array.isArray(nextState.optionValueIds)) throw new Error('Missing selection state');
        const nextProperties = [...nextPurchase.querySelectorAll('[data-dv-properties]')];
        if (properties.length !== nextProperties.length) throw new Error('Order fields changed');
        nextProperties.forEach((node, index) => node.replaceWith(properties[index]));
        const nextQuantity = nextPurchase.querySelector('[name=quantity]');
        nextQuantity.value = this.validQuantity(nextQuantity, quantity);
        const plan = nextPurchase.querySelector('[name=selling_plan]');
        if (plan && [...plan.options].some(item => item.value === oldPlan)) plan.value = oldPlan;
        this.querySelector('[data-dv-purchase]').replaceWith(nextPurchase);
        this.loading = false;
        this.failed = false;
        this.removeAttribute('aria-busy');
        if (nextState.variantId) {
          visibleUrl.searchParams.delete('option_values');
          visibleUrl.searchParams.set('variant', String(nextState.variantId));
        }
        visibleUrl.searchParams.delete('section_id');
        history.replaceState(null, '', visibleUrl.pathname + visibleUrl.search + window.location.hash);
        this.syncState();
        this.prepareSwatches();
        this.syncColorLists();
        this.observePurchase();
        const focus = [...this.querySelectorAll('[data-dv-option]')].find(node => node.dataset.position === focusKey.position && node.dataset.valueId === focusKey.id);
        focus?.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('dv:variant-change', { bubbles: true, detail: { variantId: nextState.variantId } }));
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        this.loading = false;
        this.failed = true;
        this.removeAttribute('aria-busy');
        this.querySelector('[data-dv-add]').disabled = true;
        this.querySelector('[data-dv-sticky-add]').disabled = true;
        this.showMessage(this.strings.error, visibleUrl.href);
      }
    }
    showMedia(id) {
      const slides = [...this.querySelectorAll('[data-dv-media]')];
      if (!slides.length) return;
      const next = slides.find(node => node.dataset.dvMedia === String(id)) || slides.find(node => !node.hidden) || slides[0];
      slides.forEach(node => {
        const isCurrent = node === next;
        node.hidden = !isCurrent;
        if (!isCurrent) node.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-dv-thumb]').forEach(node => node.setAttribute('aria-current', String(node.dataset.dvThumb === next.dataset.dvMedia)));
      const img = next.querySelector('img');
      const caption = img?.alt || this.querySelector('h1').textContent;
      this.querySelector('[data-dv-photo-caption]').textContent = caption;
      this.querySelector('[data-dv-photo-count]').textContent = this.strings.photoCount.replace('[current]', String(slides.indexOf(next) + 1)).replace('[count]', String(slides.length));
    }
    stepPhoto(direction) {
      const slides = [...this.querySelectorAll('[data-dv-media]')];
      if (slides.length < 2) return;
      const index = slides.findIndex(node => !node.hidden);
      this.showMedia(slides[(index + direction + slides.length) % slides.length].dataset.dvMedia);
    }
    restoreScroll() {
      if (this.previousOverflow === undefined) return;
      document.body.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }
  customElements.define('dv-product', DormVibesProduct);
})();
